import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Group, Paper, Stack, Text } from '@cowbell/ui/core';

/** Read-only mirror of the EmailComposer surface, rendered in its place once
 *  Send succeeds. Matches the legacy V1 EmailDraft card look — same blue
 *  border, inline label/value rows, body underneath — with the action
 *  buttons swapped for a "Sent" badge so the user can't accidentally
 *  double-send. */
export interface SentEmailSummaryProps {
  readonly to: string;
  readonly cc?: string;
  readonly bcc?: string;
  readonly subject: string;
  readonly body: string;
}

interface HeaderRowProps {
  readonly label: string;
  readonly value: string;
}

function HeaderRow({ label, value }: HeaderRowProps) {
  if (!value) return null;
  return (
    <Group gap="xs" align="baseline">
      <Text fz="sm" fw={500} c="dimmed" style={{ minWidth: 56 }}>
        {label}
      </Text>
      <Text fz="sm">{value}</Text>
    </Group>
  );
}

export function SentEmailSummary({
  to,
  cc = '',
  bcc = '',
  subject,
  body,
}: SentEmailSummaryProps) {
  return (
    <Paper
      radius="md"
      withBorder
      p="lg"
      style={{
        borderColor: 'var(--mantine-color-blue-3)',
        width: '100%',
      }}
    >
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <FontAwesomeIcon
            icon={faEnvelope}
            style={{
              color: 'var(--mantine-color-blue-6)',
              width: 18,
              height: 18,
            }}
          />
          <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
            EMAIL DRAFT
          </Text>
        </Group>
        <Badge variant="light" color="green" size="md" radius="sm">
          Sent
        </Badge>
      </Group>

      <Stack gap={2} mb="md">
        <HeaderRow label="Subject:" value={subject} />
        <HeaderRow label="To:" value={to} />
        <HeaderRow label="Cc:" value={cc} />
        <HeaderRow label="Bcc:" value={bcc} />
      </Stack>

      <Text
        fz="sm"
        style={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.6,
          borderTop: '1px solid var(--mantine-color-default-border)',
          paddingTop: 'var(--mantine-spacing-xs)',
        }}
      >
        {body}
      </Text>
    </Paper>
  );
}
